import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCableSubscription } from './useCableSubscription';
import { useUpdateCodeFromCable } from 'hooks/codes/mutations/useUpdateCodeFromCable';
import { codeKeys } from 'hooks/codes/queries';
import { createCodeCacheUtils } from 'hooks/utils/codeCacheUtils';
import { isCurrentSession } from 'utils/SessionIDUtil';

/**
 * CodeCable component for handling code-related cable events
 */
function CodeCable({ projectId }) {
  const queryClient = useQueryClient();
  const updateCodeFromCable = useUpdateCodeFromCable();

  const handleCodeCable = useCallback((response) => {
    // Skip processing if this response is from a different session
    // sessionID in the response is actually the session ID
    if (response.sessionID && isCurrentSession(response.sessionID)) {
      return;
    }

    if (response['action'] === 'update') {
      updateCodeFromCable(response.code);
    } else if (response['action'] === 'code_deleted') {
      // A remote delete: scrub the dead code from cached excerpt codings
      // (detail entries AND the transcript bulk copies the comparison
      // overlay classifies from) — precise, no transcript refetch storm.
      queryClient.invalidateQueries({ queryKey: codeKeys.list(projectId) });
      if (response.code_id != null) {
        createCodeCacheUtils(queryClient).removeCodeFromExcerpts(response.code_id);
      }
    } else if (response['action'] === 'codes_merged') {
      // A remote merge: rewrite the merged-away id to the survivor's.
      queryClient.invalidateQueries({ queryKey: codeKeys.list(projectId) });
      if (response.from_code_id != null && response.to_code_id != null) {
        createCodeCacheUtils(queryClient).mergeCodeInExcerpts(response.from_code_id, response.to_code_id);
      }
    } else if (response['action'] === 'nest' || response['action'] === 'project_changed') {
      // Nest / create / recolor reorganizations: codings are untouched, so
      // the codes list refetch is all that's needed.
      queryClient.invalidateQueries({
        queryKey: codeKeys.list(projectId)
      });
    }
  }, [updateCodeFromCable, queryClient, projectId]);

  useCableSubscription({
    channel: { channel: 'CodesChannel', project: projectId },
    onReceived: handleCodeCable
  });

  return null;
}

export default CodeCable;